import { z } from 'zod';
import { DataSource } from './car-enums';
/**
 * Source registry contract (Phase 15 — source abstraction).
 *
 * One document per auction source in the `sources` collection. The registry
 * turns the hard-coded `data_source` discriminator into operable config: which
 * sources are enabled, on what schedule, at which scrape tier, and — critically
 * — a `priority` that governs cross-source VIN-merge conflict resolution in the
 * ingest service.
 *
 * SECURITY INVARIANT: `credentialsRef` is a CONFIG PATH STRING (e.g.
 * "config/secrets.local.json#wom" or an env var NAME), NEVER a secret value.
 * No credential material is ever stored in this collection. The scraper session
 * a source drives MUST use a throwaway account — the bidding account is never
 * loaded into a scraper (enforced by scripts/check-bidding-account-isolation).
 */
/**
 * Scrape tier — maps a source to the fallback chain rung it is served from.
 * See docs/scraping-tiers.md (repo root).
 *   0 — curl_cffi fast-path (cheapest, TLS-impersonation, no browser)
 *   1 — local-Mac hermes (Claude-in-Chrome, warm session). The hermes service
 *       was removed on 2026-10-04; the value stays because it is persisted in
 *       the `sources` registry.
 *   2 — cloud anti-detect warm standby (residential proxy + persisted cf_clearance)
 *   3 — managed scraping API (last resort, paid)
 */
export const ScrapeTier = {
    CURL_CFFI: 0,
    LOCAL_HERMES: 1,
    CLOUD_ANTIDETECT: 2,
    MANAGED_API: 3,
};
/** Operational health of a source, surfaced by GET /api/admin/sources. */
export const SourceHealthStatus = {
    HEALTHY: 'healthy',
    DEGRADED: 'degraded',
    STALE: 'stale',
    DOWN: 'down',
    UNKNOWN: 'unknown',
};
export const SourceSchema = z.object({
    /** Stable discriminator key — must be a member of the DataSource enum. */
    key: z.nativeEnum(DataSource),
    displayName: z.string().min(1),
    enabled: z.boolean().default(false),
    /**
     * Config PATH pointer to credentials — NEVER the secret itself. Examples:
     * "config/secrets.local.json#wom", "env:WOM_SCRAPER_TOKEN". Nullable for
     * sources that need no auth (public listings).
     */
    credentialsRef: z.string().nullish(),
    /** Cron-ish schedule string, human-owned (e.g. "even-hours 08-22 Europe/Kyiv"). */
    schedule: z.string().nullish(),
    /** Fallback-chain rung this source is served from (0–3). */
    scrapeTier: z.nativeEnum(ScrapeTier).default(ScrapeTier.LOCAL_HERMES),
    /**
     * Cross-source merge priority. On a VIN collision between two sources, the
     * HIGHER priority wins the core fields; the loser is appended to the car's
     * `source_listings[]`. Higher = more authoritative.
     */
    priority: z.number().int().default(0),
    healthStatus: z.nativeEnum(SourceHealthStatus).default(SourceHealthStatus.UNKNOWN),
    /** Fed by the ingest hook — timestamp of the last accepted lot from this source. */
    lastIngestAt: z.coerce.date().nullish(),
    /** Denormalized count of lots currently carrying this source. */
    lotCount: z.number().int().nonnegative().default(0),
    createdAt: z.coerce.date().nullish(),
    updatedAt: z.coerce.date().nullish(),
});
/** Admin create payload — key + displayName required, the rest defaulted. */
export const SourceCreateSchema = SourceSchema.pick({
    key: true,
    displayName: true,
    enabled: true,
    credentialsRef: true,
    schedule: true,
    scrapeTier: true,
    priority: true,
});
/** Admin update payload — every field optional except the immutable `key`. */
export const SourceUpdateSchema = SourceCreateSchema.partial().omit({ key: true });
/**
 * A per-source listing record kept on a Car when >1 source lists the same VIN.
 * The winning source owns the Car's core fields; every source that ever
 * ingested the VIN (including the winner) is represented here so the UI can
 * show "also on WOM: CHF 14'900".
 */
export const SourceListingSchema = z.object({
    data_source: z.nativeEnum(DataSource),
    listing_id: z.string(),
    url: z.string().nullish(),
    /** Kept as string to match the Car.current_bid quirk (CHF, never a number). */
    current_bid: z.string().nullish(),
    auction_name: z.string().nullish(),
    auction_end_date: z.coerce.date().nullish(),
    scraped_at: z.coerce.date().nullish(),
    /** Snapshot of the source's priority at merge time — resolves winner cheaply. */
    priority: z.number().int().default(0),
});
//# sourceMappingURL=source.js.map